import React, { useEffect, useMemo } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Table from '@amzn/awsui-components-console/table';
import { useDescribeStringGroupVersionLazyQuery } from 'src/generated/graphql';
import { StringInfo, TranslationStatus } from '../types';
import { computeVersionDiff, DiffResult } from '../utils/versionDiff';
import styles from '../StringGroupDetail.module.scss';

interface DiffModalProps {
  visible: boolean;
  version: number;
  namespaceId: string;
  stringGroupId: string;
  onDismiss: () => void;
}

function getBadgeColor(changeType: string): 'green' | 'red' | 'blue' | 'grey' {
  switch (changeType) {
    case 'added':
      return 'green';
    case 'removed':
      return 'red';
    case 'changed':
      return 'blue';
    // istanbul ignore next -- defensive fallback; changeType is only ever 'added' | 'removed' | 'changed'
    default:
      return 'grey';
  }
}

/** Maps the GraphQL StringInfo response to the local StringInfo type. */
function mapToStringInfo(
  gqlStrings: ReadonlyArray<{
    stringId: string;
    sourceLocale: string;
    sourceText: string;
    contextNote?: string | null;
    inlineMarkupType?: string | null;
    messageFormatType?: string | null;
    variants: ReadonlyArray<{
      cxId: string;
      sourceText: string;
      contextNote?: string | null;
    }>;
  }>,
): StringInfo[] {
  return gqlStrings.map((s) => ({
    stringId: s.stringId,
    sourceLocale: s.sourceLocale,
    sourceText: s.sourceText,
    contextNote: s.contextNote ?? undefined,
    inlineMarkupType:
      (s.inlineMarkupType as StringInfo['inlineMarkupType']) ?? undefined,
    messageFormatType:
      (s.messageFormatType as StringInfo['messageFormatType']) ?? undefined,
    clientId: 'unknown',
    variants: s.variants.map((v) => ({
      cxId: v.cxId,
      sourceText: v.sourceText,
      contextNote: v.contextNote ?? undefined,
    })),
    translationStatus: TranslationStatus.Complete,
  }));
}

const DiffModal: React.FC<DiffModalProps> = ({
  visible,
  version,
  namespaceId,
  stringGroupId,
  onDismiss,
}) => {
  const previousVersion = version - 1;

  const [
    fetchPrevious,
    { data: prevData, loading: prevLoading, error: prevError },
  ] = useDescribeStringGroupVersionLazyQuery();
  const [
    fetchCurrent,
    { data: currData, loading: currLoading, error: currError },
  ] = useDescribeStringGroupVersionLazyQuery();

  // DescribeStringGroup expands every TRANSCOM page when includeStrings=true.
  useEffect(() => {
    if (!visible || previousVersion < 1) return;
    fetchPrevious({
      variables: {
        namespaceId,
        stringGroupId,
        stringGroupVersion: previousVersion.toString(),
      },
      fetchPolicy: 'network-only',
    });
    fetchCurrent({
      variables: {
        namespaceId,
        stringGroupId,
        stringGroupVersion: version.toString(),
      },
      fetchPolicy: 'network-only',
    });
  }, [
    visible,
    version,
    previousVersion,
    namespaceId,
    stringGroupId,
    fetchPrevious,
    fetchCurrent,
  ]);

  const loading = prevLoading || currLoading;
  const error = prevError || currError;

  // Apollo retains the previous open's results, and the refetch only fires
  // from a post-paint effect — so on the first render after reopening for a
  // different version, `loading` is false and the retained data belongs to the
  // version viewed before. Comparing the returned versions against the
  // requested ones keeps that stale snapshot out of the table.
  // Compare numerically on both sides: `version` may arrive as a string at
  // runtime (the schema exposes it as String), so `Number(...) === version`
  // would be `number === string` and never match, leaving the spinner stuck.
  // `previousVersion` is already numeric (derived via `version - 1`).
  const dataMatchesRequest =
    Number(prevData?.describeStringGroup.version) === previousVersion &&
    Number(currData?.describeStringGroup.version) === Number(version);

  const diffResults: DiffResult[] = useMemo(() => {
    if (!dataMatchesRequest || !prevData || !currData) return [];
    const oldStrings: StringInfo[] = mapToStringInfo(
      prevData.describeStringGroup.strings,
    );
    const newStrings: StringInfo[] = mapToStringInfo(
      currData.describeStringGroup.strings,
    );
    return computeVersionDiff(oldStrings, newStrings);
  }, [prevData, currData, dataMatchesRequest]);

  const addedCount = diffResults.filter((d) => d.changeType === 'added').length;
  const removedCount = diffResults.filter(
    (d) => d.changeType === 'removed',
  ).length;
  const changedCount = diffResults.filter(
    (d) => d.changeType === 'changed',
  ).length;

  const renderContent = () => {
    // The modal always mounts its children (Cloudscape hides via CSS when
    // not visible), and the fetches only fire while visible — so render
    // nothing until the modal is actually open. This also prevents the
    // data-gated spinner below from showing in the hidden tree.
    if (!visible) {
      return null;
    }

    if (previousVersion < 1) {
      return (
        <Alert type="info">
          Cannot show diff — this is the first version (no previous version
          exists).
        </Alert>
      );
    }

    if (error) {
      return (
        <Alert type="error">Failed to load version data: {error.message}</Alert>
      );
    }

    // Gate on the data as well as `loading`: the fetches fire from a
    // post-paint effect, so on the first render `loading` is still false and
    // the data is either undefined (first open) or the previously viewed
    // version's (reopen). Without this guard the table would briefly flash
    // "No differences found" or the previous version's diff before the
    // spinner appears. (Error is checked first so a failed fetch surfaces the
    // alert rather than an indefinite spinner.)
    if (loading || !dataMatchesRequest) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
            Loading version data…
          </Box>
        </Box>
      );
    }

    return (
      <SpaceBetween size="m">
        <Alert type="info">
          <strong>Change summary:</strong> {addedCount} added, {removedCount}{' '}
          removed, {changedCount} changed
        </Alert>

        <Table
          columnDefinitions={[
            {
              id: 'stringId',
              header: 'String ID',
              cell: (item: DiffResult) => (
                <Box fontWeight="bold">{item.stringId}</Box>
              ),
              width: 200,
            },
            {
              id: 'change',
              header: 'Change',
              cell: (item: DiffResult) => (
                <Badge color={getBadgeColor(item.changeType)}>
                  {item.changeType}
                </Badge>
              ),
              width: 100,
            },
            {
              id: 'previous',
              header: `Previous (v${previousVersion})`,
              cell: (item: DiffResult) => (
                <span
                  className={
                    item.changeType === 'removed'
                      ? styles.diffRemoved
                      : undefined
                  }
                >
                  {item.previous?.sourceText ?? '—'}
                </span>
              ),
            },
            {
              id: 'current',
              header: `Current (v${version})`,
              cell: (item: DiffResult) => (
                <span
                  className={
                    item.changeType === 'added' ? styles.diffAdded : undefined
                  }
                >
                  {item.current?.sourceText ?? '—'}
                </span>
              ),
            },
          ]}
          items={diffResults}
          stripedRows
          variant="embedded"
          empty={
            <Box textAlign="center" color="inherit" padding="l">
              No differences found between versions.
            </Box>
          }
        />
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={`Diff: v${previousVersion} → v${version}`}
    >
      {renderContent()}
    </Modal>
  );
};

export default DiffModal;
